import Konva from 'konva';
import { clampZoom, clone } from '@/utils/board/boardUtils';
export const draftStateAfterUpdate = (state, elements) => {
    var _a, _b, _c;
    const newState = clone(state);
    for (const layer of newState.layers)
        for (const layer_el of layer.elements) {
            const matched_element = elements.find((el) => el.id === layer_el.id);
            if (matched_element) {
                layer_el.props = Object.assign(Object.assign({}, layer_el.props), matched_element.props);
                if (matched_element.zIndex !== undefined) {
                    layer_el.zIndex = matched_element.zIndex;
                }
                if (matched_element.locked !== undefined) {
                    layer_el.locked = matched_element.locked;
                }
                if (matched_element.description !== undefined) {
                    layer_el.description = matched_element.description;
                }
                if (matched_element.name !== undefined) {
                    layer_el.name = matched_element.name;
                }
                layer_el.zIndex = (_a = matched_element.zIndex) !== null && _a !== void 0 ? _a : layer_el.zIndex;
                layer_el.presentationOrder =
                    (_b = matched_element.presentationOrder) !== null && _b !== void 0 ? _b : layer_el.presentationOrder;
                layer_el.isPresentationFrame =
                    (_c = matched_element.isPresentationFrame) !== null && _c !== void 0 ? _c : layer_el.isPresentationFrame;
            }
        }
    return newState;
};
/**
 * Масштабирует маску элемента пропорционально изменению размера элемента
 *
 * @param element - Элемент с маской
 * @param oldWidth - Старая ширина элемента
 * @param oldHeight - Старая высота элемента
 * @param newWidth - Новая ширина элемента
 * @param newHeight - Новая высота элемента
 * @returns Обновленные свойства элемента с масштабированной маской
 */
export const scaleElementMask = (element, oldWidth, oldHeight, newWidth, newHeight) => {
    var _a, _b;
    const clip = (_a = element.props) === null || _a === void 0 ? void 0 : _a.clip;
    if (!clip) {
        return { id: element.id, props: {} };
    }
    // Вычисляем коэффициенты масштабирования
    const scaleX = newWidth / oldWidth;
    const scaleY = newHeight / oldHeight;
    if (clip.type === 'rect') {
        // Масштабируем прямоугольную маску
        const scaledClip = {
            type: 'rect',
            x: (clip.x || 0) * scaleX,
            y: (clip.y || 0) * scaleY,
            w: (clip.w || oldWidth) * scaleX,
            h: (clip.h || oldHeight) * scaleY,
        };
        return {
            id: element.id,
            props: {
                clip: scaledClip,
            },
        };
    }
    if (clip.type === 'circle') {
        // Масштабируем круглую маску
        const oldRadius = (_b = clip.r) !== null && _b !== void 0 ? _b : Math.min(oldWidth, oldHeight) / 2;
        const scaleRadius = Math.min(scaleX, scaleY); // Используем минимальный коэффициент для сохранения формы круга
        const scaledClip = {
            type: 'circle',
            r: oldRadius * scaleRadius,
        };
        return {
            id: element.id,
            props: {
                clip: scaledClip,
            },
        };
    }
    return { id: element.id, props: {} };
};
/**
 * Перевод координат из экрана в "мир" доски с учётом zoom и позиции
 */
export function toWorld(stage, point) {
    if (!stage)
        return point;
    const scale = stage.scaleX();
    const x = (point.x - stage.x()) / scale;
    const y = (point.y - stage.y()) / scale;
    return { x, y };
}
/**
 * Получить элемент и его слой по id
 */
export function getElementsMap(layers = []) {
    const map = new Map();
    for (const layer of layers) {
        for (const element of layer.elements || []) {
            map.set(element.id, { el: element, layer });
        }
    }
    return map;
}
/**
 * Ограничение размеров трансформации
 */
export function transformBoundBoxFunc(oldBox, newBox, zoom) {
    const minSizeInWorld = 10;
    const minSizeOnScreen = minSizeInWorld * zoom;
    if (newBox.width <= minSizeOnScreen || newBox.height <= minSizeOnScreen)
        return oldBox;
    return newBox;
}
/* Привязка к сетке */
const snap = (v, gridSize, snapToGrid, exact = false, offset = 0) => {
    const snapStep = exact ? gridSize : gridSize / 8;
    // Если найдена локальная сетка (exact = true), принудительно применяем снаппинг
    // независимо от глобального флага snapToGrid
    if (!snapToGrid && !exact)
        return v;
    const relativeV = v - offset;
    const snappedRelative = Math.round(relativeV / snapStep) * snapStep;
    return offset + snappedRelative;
};
/**
 * Получить координаты с привязкой к сетке
 * - привязка к локальной сетке, если элемент находится в пределах элемента с локальной сеткой
 * - привязка к глобальной сетке, если элемент находится вне всех элементов с локальной сеткой
 */
export function getSnappedXY(stage, x, y, gridSize, snapToGrid, layers = [], elementId) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    if (!stage)
        return { x, y };
    const scale = stage.scaleX();
    const { x: worldX, y: worldY } = toWorld(stage, { x, y });
    let exactSnapping = false;
    let offsetX = 0;
    let offsetY = 0;
    // Найти элемент под позицией (исключая перемещаемый элемент) по bounding box
    let effectiveGridSize = gridSize;
    for (const layer of layers) {
        for (const el of layer.elements) {
            if (el.id === elementId)
                continue; // Исключить самого себя
            const elX = (_b = (_a = el.props) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : 0;
            const elY = (_d = (_c = el.props) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : 0;
            const elW = (_f = (_e = el.props) === null || _e === void 0 ? void 0 : _e.w) !== null && _f !== void 0 ? _f : 0;
            const elH = (_h = (_g = el.props) === null || _g === void 0 ? void 0 : _g.h) !== null && _h !== void 0 ? _h : 0;
            if (worldX >= elX && worldX <= elX + elW && worldY >= elY && worldY <= elY + elH) {
                if ((_k = (_j = el.props) === null || _j === void 0 ? void 0 : _j.localGrid) === null || _k === void 0 ? void 0 : _k.enabled) {
                    effectiveGridSize = (_l = el.props.localGrid.size) !== null && _l !== void 0 ? _l : gridSize;
                    exactSnapping = true; // Всегда точный snap для локальной сетки
                    offsetX = elX;
                    offsetY = elY;
                    break; // Найден элемент с локальной сеткой, используем его
                }
            }
        }
        if (effectiveGridSize !== gridSize)
            break; // Если нашли, выходим из цикла слоёв
    }
    const snappedWorldX = snap(worldX, effectiveGridSize, snapToGrid, exactSnapping, offsetX);
    const snappedWorldY = snap(worldY, effectiveGridSize, snapToGrid, exactSnapping, offsetY);
    return {
        x: snappedWorldX * scale + stage.x(),
        y: snappedWorldY * scale + stage.y(),
    };
}
/**
 * Вычисляет offset элемента для центрирования вращения вокруг центра элемента
 *
 * @param width - Ширина элемента
 * @param height - Высота элемента
 * @returns Объект с offsetX и offsetY
 */
export function getElementOffset(width, height) {
    return {
        offsetX: width / 2,
        offsetY: height / 2,
    };
}
/**
 * Получает offset элемента из узла Konva или вычисляет его на основе размеров элемента
 *
 * @param node - Узел Konva
 * @param elementWidth - Ширина элемента (используется как fallback)
 * @param elementHeight - Высота элемента (используется как fallback)
 * @returns Объект с offsetX и offsetY
 */
export function getNodeOffset(node, elementWidth, elementHeight) {
    const nodeOffsetX = node.offsetX();
    const nodeOffsetY = node.offsetY();
    return {
        offsetX: nodeOffsetX !== null && nodeOffsetX !== void 0 ? nodeOffsetX : elementWidth / 2,
        offsetY: nodeOffsetY !== null && nodeOffsetY !== void 0 ? nodeOffsetY : elementHeight / 2,
    };
}
/**
 * Вычисляет реальную позицию элемента (левый верхний угол) из позиции узла Konva
 *
 * @param nodeX - Позиция X узла Konva (с offset)
 * @param nodeY - Позиция Y узла Konva (с offset)
 * @param offsetX - Offset X элемента
 * @param offsetY - Offset Y элемента
 * @returns Реальная позиция элемента (без offset)
 */
export function getElementPositionFromNode(nodeX, nodeY, offsetX, offsetY) {
    return {
        x: nodeX - offsetX,
        y: nodeY - offsetY,
    };
}
/**
 * Вычисляет позицию узла Konva (с offset) из реальной позиции элемента
 *
 * @param elementX - Реальная позиция X элемента (без offset)
 * @param elementY - Реальная позиция Y элемента (без offset)
 * @param offsetX - Offset X элемента
 * @param offsetY - Offset Y элемента
 * @returns Позиция узла Konva (с offset)
 */
export function getNodePositionFromElement(elementX, elementY, offsetX, offsetY) {
    return {
        x: elementX + offsetX,
        y: elementY + offsetY,
    };
}
/**
 * Преобразует world координаты в экранные координаты
 *
 * @param stage - Stage Konva
 * @param worldX - X координата в world координатах
 * @param worldY - Y координата в world координатах
 * @returns Координаты в экранных координатах
 */
export function worldToScreen(stage, worldX, worldY) {
    const scale = stage.scaleX();
    return {
        x: worldX * scale + stage.x(),
        y: worldY * scale + stage.y(),
    };
}
/**
 * Преобразует экранные координаты в world координаты
 *
 * @param stage - Stage Konva
 * @param screenX - X координата в экранных координатах
 * @param screenY - Y координата в экранных координатах
 * @returns Координаты в world координатах
 */
export function screenToWorld(stage, screenX, screenY) {
    const scale = stage.scaleX();
    return {
        x: (screenX - stage.x()) / scale,
        y: (screenY - stage.y()) / scale,
    };
}
/**
 * Перемещение выделенных элементов на dx, dy шагов сетки с snap
 */
export function moveSelectedElements(selectedIds, visibleElements, boardState, gridSize, snapToGrid, stage, setBoardState, debouncedSend, dx, dy) {
    if (!selectedIds.length || !stage)
        return;
    const scale = stage.scaleX();
    const stageX = stage.x();
    const stageY = stage.y();
    const payload = [];
    const sendPayload = [];
    selectedIds.forEach((id) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        const el = visibleElements.find((e) => e.id === id);
        if (!el)
            return;
        const currentX = (_b = (_a = el.props) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : 0;
        const currentY = (_d = (_c = el.props) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : 0;
        // Определяем effectiveGridSize для элемента
        let effectiveGridSize = gridSize;
        for (const layer of boardState.layers) {
            for (const element of layer.elements) {
                if (element.id === id)
                    continue;
                const elX = (_f = (_e = element.props) === null || _e === void 0 ? void 0 : _e.x) !== null && _f !== void 0 ? _f : 0;
                const elY = (_h = (_g = element.props) === null || _g === void 0 ? void 0 : _g.y) !== null && _h !== void 0 ? _h : 0;
                const elW = (_k = (_j = element.props) === null || _j === void 0 ? void 0 : _j.w) !== null && _k !== void 0 ? _k : 0;
                const elH = (_m = (_l = element.props) === null || _l === void 0 ? void 0 : _l.h) !== null && _m !== void 0 ? _m : 0;
                if (currentX >= elX && currentX <= elX + elW && currentY >= elY && currentY <= elY + elH) {
                    if ((_p = (_o = element.props) === null || _o === void 0 ? void 0 : _o.localGrid) === null || _p === void 0 ? void 0 : _p.enabled) {
                        effectiveGridSize = (_q = element.props.localGrid.size) !== null && _q !== void 0 ? _q : gridSize;
                        break;
                    }
                }
            }
            if (effectiveGridSize !== gridSize)
                break;
        }
        // Новая позиция в world coordinates: двигаем на dx/dy шагов сетки
        const newWorldX = currentX + dx * effectiveGridSize;
        const newWorldY = currentY + dy * effectiveGridSize;
        // Snap в screen coordinates
        const snappedScreen = getSnappedXY(stage, newWorldX * scale + stageX, // Преобразуем в screen
        newWorldY * scale + stageY, gridSize, snapToGrid, boardState.layers, id);
        // Обратно в world coordinates
        const snappedWorldX = (snappedScreen.x - stageX) / scale;
        const snappedWorldY = (snappedScreen.y - stageY) / scale;
        payload.push({ id, props: { x: snappedWorldX, y: snappedWorldY } });
        sendPayload.push({ id, x: snappedWorldX, y: snappedWorldY });
    });
    setBoardState((prev) => draftStateAfterUpdate(prev, payload));
    // Debounced отправка на сервер
    debouncedSend(sendPayload);
}
/**
 * Определяет, является ли цель элементом доски
 *
 * @param target - Цель события
 * @returns true, если цель является элементом доски
 */
export const isElementShape = (target) => {
    return (!(target instanceof Konva.Transformer) &&
        (target instanceof Konva.Shape ||
            target instanceof Konva.Group ||
            target instanceof Konva.Text ||
            target instanceof Konva.Image));
};
/**
 * Получает все видимые элементы на экране, которые подходят по типу isElementShape
 *
 * @param stage - Сцена Konva
 * @param buffer - Буферная зона в пикселях для расширения области поиска (по умолчанию 50px)
 * @returns Массив видимых элементов
 */
export const getVisibleElementsOnScreenSync = (stage, buffer = 50) => {
    if (!stage) {
        return [];
    }
    // Получаем область видимости сцены
    const scale = stage.scaleX();
    const stageX = stage.x();
    const stageY = stage.y();
    const stageWidth = stage.width();
    const stageHeight = stage.height();
    // Вычисляем область видимости в координатах сцены
    // Учитываем масштаб для правильного расчета
    const viewportLeft = -stageX / scale;
    const viewportTop = -stageY / scale;
    const viewportRight = viewportLeft + stageWidth / scale;
    const viewportBottom = viewportTop + stageHeight / scale;
    // Расширяем область видимости для снаппинга
    const searchLeft = viewportLeft - buffer;
    const searchTop = viewportTop - buffer;
    const searchRight = viewportRight + buffer;
    const searchBottom = viewportBottom + buffer;
    const visibleElements = [];
    // Получаем все элементы на сцене (Shepes и Groups с name='element')
    const elements = stage.find('.element');
    for (const el of elements) {
        // Проверяем, является ли элемент подходящим типом
        if (!isElementShape(el))
            continue;
        // Проверяем видимость элемента
        if (!el.visible())
            continue;
        // Получаем позицию элемента в локальных координатах сцены
        const elementPos = el.position();
        const elementX = elementPos.x;
        const elementY = elementPos.y;
        // Получаем размеры элемента
        const nodeBox = el.getClientRect({ skipStroke: true });
        const elementWidth = nodeBox.width;
        const elementHeight = nodeBox.height;
        // Вычисляем границы элемента в локальных координатах
        const nodeX = elementX;
        const nodeY = elementY;
        const nodeRight = nodeX + elementWidth;
        const nodeBottom = nodeY + elementHeight;
        // Проверяем пересечение с областью видимости
        const intersects = nodeRight >= searchLeft &&
            nodeX <= searchRight &&
            nodeBottom >= searchTop &&
            nodeY <= searchBottom;
        if (intersects) {
            visibleElements.push(el);
        }
    }
    return visibleElements;
};
/**
 * Выполняет зум с центрированием относительно указанной точки
 *
 * @param currentZoom - Текущий масштаб
 * @param currentPosition - Текущая позиция сцены
 * @param centerPoint - Точка, относительно которой выполняется зум (в экранных координатах)
 * @param zoomFactor - Коэффициент зума (например, 1.1 для увеличения, 0.9 для уменьшения)
 * @returns Новые значения зума и позиции сцены
 */
export function zoomWithCenter(currentZoom, currentPosition, centerPoint, zoomFactor) {
    const newZoom = clampZoom(currentZoom * zoomFactor);
    // Вычисляем точку в мировых координатах относительно центра зума
    const mousePointTo = {
        x: (centerPoint.x - currentPosition.x) / currentZoom,
        y: (centerPoint.y - currentPosition.y) / currentZoom,
    };
    // Пересчитываем позицию сцены для сохранения центра зума
    const newPosition = {
        x: centerPoint.x - mousePointTo.x * newZoom,
        y: centerPoint.y - mousePointTo.y * newZoom,
    };
    return { newZoom, newPosition };
}
